import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaSync, FaClock, FaTrophy, FaFutbol, FaExclamationTriangle, FaFilter, FaCalendar, FaPen, FaGift, FaGlobe } from 'react-icons/fa';
import { toast } from 'react-hot-toast';
import ParticipationModal from '../components/UI/ParticipationModal';
import WelcomeModal from '../components/UI/WelcomeModal';
import MatchCard from '../components/UI/MatchCard';
import matchService from '../services/matchService';
import apiService from '../services/api';
import '../styles/Home.css';

const Home = () => {
    const [matches, setMatches] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedMatch, setSelectedMatch] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [participatedMatches, setParticipatedMatches] = useState(new Set());
    const [language, setLanguage] = useState('fr');
    const [config, setConfig] = useState({
        gigasOffered: 1,
        participationFee: 100
    });
    const [filters, setFilters] = useState({
        competition: 'all',
        status: 'all',
        date: 'all'
    });
    const [isWelcomeModalOpen, setIsWelcomeModalOpen] = useState(false);

    const fetchConfig = async () => {
        try {
            const response = await apiService.getConfig();
            if (response.success) {
                setConfig(response.data);
            }
        } catch (error) {
            console.error('Erreur lors du chargement de la configuration:', error);
            // Utiliser les valeurs par défaut en cas d'erreur
        }
    };

    const fetchMatches = async () => {
        try {
            console.log('🏠 Home: Début de la récupération des matchs...');
            setLoading(true);
            setError(null);
            const data = await matchService.getMatchesWithCache();
            console.log('🏠 Home: Données reçues:', data);
            
            // Validation et nettoyage des données
            const validatedMatches = Array.isArray(data) ? data.filter(match => {
                // Vérifier que le match a les propriétés essentielles
                const isValid = match && match.id && (match.homeTeam || match.homeTeamName) && (match.awayTeam || match.awayTeamName);
                if (!isValid) {
                    console.warn('⚠️ Match invalide ignoré:', match);
                }
                return isValid;
            }) : [];
            
            console.log(`✅ Home: ${validatedMatches.length} matchs valides sur ${data?.length || 0} reçus`);
            setMatches(validatedMatches);
            
            // Pas d'erreur si on reçoit un tableau vide (pas de matchs disponibles)
            if (Array.isArray(data) && data.length === 0) {
                console.log('ℹ️ Home: Aucun match disponible actuellement');
                // Ne pas afficher d'erreur, juste un tableau vide
            }
        } catch (error) {
            console.error('❌ Home: Erreur fetchMatches:', error);
            
            // Messages d'erreur plus clairs selon le type d'erreur
            let errorMessage = error.message;
            
            if (error.message.includes('Failed to fetch')) {
                errorMessage = language === 'fr' 
                    ? 'Impossible de se connecter au serveur. Vérifie ta connexion internet et réessaie ! 🌐'
                    : 'Unable to connect to server. Check your internet connection and try again! 🌐';
            } else if (error.message.includes('NetworkError')) {
                errorMessage = language === 'fr'
                    ? 'Problème de réseau. Vérifie ta connexion et réessaie ! 📡'
                    : 'Network problem. Check your connection and try again! 📡';
            } else if (error.message.includes('timeout')) {
                errorMessage = language === 'fr'
                    ? 'Le serveur met trop de temps à répondre. Réessaie dans quelques instants ! ⏰'
                    : 'Server is taking too long to respond. Try again in a few moments! ⏰';
            }
            
            setError(errorMessage);
            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchConfig();
        fetchMatches();
        
        // Afficher le modal de bienvenue après un délai (à chaque visite)
        const timer = setTimeout(() => {
            setIsWelcomeModalOpen(true);
        }, 2000); // 2 secondes après le chargement
        
        return () => clearTimeout(timer);
    }, []);

    const handleRefresh = () => {
        console.log('🔄 Rafraîchissement des données...');
        matchService.clearCache();
        // Réinitialiser les filtres
        setFilters({
            competition: 'all',
            status: 'all',
            date: 'all'
        });
        fetchMatches();
    };

    const handleParticipate = (match) => {
        if (participatedMatches.has(match.id)) {
            toast.error('Tu as déjà participé à ce match !');
            return;
        }
        // Google Analytics event: click participate
        if (window.gtag) {
            window.gtag('event', 'click_participate', {
                match_id: match.id,
                competition_code: match.competition?.code,
                competition_name: match.competition?.name
            });
        }
        setSelectedMatch(match);
        setIsModalOpen(true);
    };

    const handleModalClose = () => {
        setIsModalOpen(false);
        setSelectedMatch(null);
    };

    const handleParticipationSuccess = (matchId) => {
        setParticipatedMatches(prev => new Set([...prev, matchId]));
        toast.success('Participation validée ! Résultat par SMS sous 24h.');
    };

    const handleWelcomeModalClose = () => {
        setIsWelcomeModalOpen(false);
    };

    // Fonction utilitaire pour normaliser les dates
    const normalizeDate = (date) => {
        if (!date) return new Date();
        const normalized = new Date(date);
        if (isNaN(normalized.getTime())) {
            console.warn('Date invalide détectée:', date);
            return new Date(); // Fallback vers maintenant
        }
        // Normaliser à minuit dans le fuseau horaire local
        const localDate = new Date(normalized.getFullYear(), normalized.getMonth(), normalized.getDate());
        return localDate;
    };

    // Fonction utilitaire pour vérifier si un match est en cours
    const isMatchLive = (matchTime) => {
        if (!matchTime || isNaN(matchTime.getTime())) {
            console.warn('Heure de match invalide:', matchTime);
            return false;
        }
        const now = new Date();
        const matchStart = new Date(matchTime);
        const matchEnd = new Date(matchStart.getTime() + 2 * 60 * 60 * 1000); // 2 heures après
        return matchStart <= now && now <= matchEnd;
    };

    // Fonction utilitaire pour vérifier si un match est à venir
    const isMatchUpcoming = (matchTime) => {
        if (!matchTime || isNaN(matchTime.getTime())) {
            console.warn('Heure de match invalide:', matchTime);
            return false;
        }
        const now = new Date();
        const matchStart = new Date(matchTime);
        return matchStart > now;
    };

    // Fonction utilitaire pour vérifier si un match est terminé
    const isMatchFinished = (matchTime) => {
        if (!matchTime || isNaN(matchTime.getTime())) {
            console.warn('Heure de match invalide:', matchTime);
            return false;
        }
        const now = new Date();
        const matchEnd = new Date(matchTime.getTime() + 2 * 60 * 60 * 1000); // 2 heures après
        return now > matchEnd;
    };

    // Fonction utilitaire pour extraire le code de compétition de façon robuste
    const getCompetitionCode = (match) =>
        (match.competition?.code || match.competition || match.competitionId || '').toUpperCase().trim();

    // Liste statique de toutes les compétitions (synchronisée avec le backend)
    const allCompetitions = [
        { code: 'WC', name: 'Coupe du Monde' },
        { code: 'CL', name: 'Champions League' },
        { code: 'CLI', name: 'Copa Libertadores' },
        { code: 'BL1', name: 'Bundesliga' },
        { code: 'DED', name: 'Eredivisie' },
        { code: 'BSA', name: 'Brasileiro Série A' },
        { code: 'PD', name: 'La Liga' },
        { code: 'FL1', name: 'Ligue 1' },
        { code: 'ELC', name: 'Championship' },
        { code: 'PPL', name: 'Primeira Liga' },
        { code: 'EC', name: 'Euro' },
        { code: 'SA', name: 'Serie A' },
        { code: 'PL', name: 'Premier League' }
    ];

    const getFilteredMatches = () => {
        let filtered = [...matches];
        
        console.log('🔍 Filtrage des matchs:', {
            totalMatches: matches.length,
            filters: filters,
            competitions: matches.map(m => m.competition?.code || m.competition).filter(Boolean),
            sampleMatch: matches[0] ? {
                id: matches[0].id,
                competition: matches[0].competition?.code || matches[0].competition,
                scheduledTime: matches[0].scheduledTime,
                status: matches[0].status
            } : null
        });

        // Filtre par compétition - Comparaison robuste
        if (filters.competition !== 'ALL' && filters.competition !== 'all') {
            filtered = filtered.filter(match => getCompetitionCode(match) === filters.competition.toUpperCase());
        }

        // Filtre par statut - Logique temporelle corrigée
        if (filters.status !== 'all') {
            const beforeFilter = filtered.length;
            filtered = filtered.filter(match => {
                const matchTime = new Date(match.scheduledTime || match.utcDate);
                
                switch (filters.status) {
                    case 'upcoming':
                        return isMatchUpcoming(matchTime);
                    case 'live':
                        return isMatchLive(matchTime);
                    case 'finished':
                        return isMatchFinished(matchTime);
                    default:
                        return true;
                }
            });
            console.log(`⏰ Filtre statut "${filters.status}": ${beforeFilter} → ${filtered.length} matchs`);
        }

        // Filtre par date - Comparaison de plages corrigée
        if (filters.date !== 'all') {
            const beforeFilter = filtered.length;
            const today = normalizeDate(new Date());
            const tomorrow = new Date(today);
            tomorrow.setDate(tomorrow.getDate() + 1);
            
            filtered = filtered.filter(match => {
                const matchDate = normalizeDate(match.scheduledTime || match.utcDate);
                
                switch (filters.date) {
                    case 'today': {
                        const nextDay = new Date(today);
                        nextDay.setDate(nextDay.getDate() + 1);
                        return matchDate >= today && matchDate < nextDay;
                    }
                    case 'tomorrow': {
                        const dayAfterTomorrow = new Date(tomorrow);
                        dayAfterTomorrow.setDate(dayAfterTomorrow.getDate() + 1);
                        return matchDate >= tomorrow && matchDate < dayAfterTomorrow;
                    }
                    case 'week': {
                        const weekFromNow = new Date(today);
                        weekFromNow.setDate(weekFromNow.getDate() + 7);
                        // Seulement les matchs futurs dans la semaine
                        return matchDate >= today && matchDate < weekFromNow;
                    }
                    default:
                        return true;
                }
            });
            console.log(`📅 Filtre date "${filters.date}": ${beforeFilter} → ${filtered.length} matchs`);
        }

        console.log(`✅ Résultat final: ${filtered.length} matchs filtrés`);
        return filtered;
    };

    const filteredMatches = getFilteredMatches();

    const toggleLanguage = () => {
        setLanguage(prev => prev === 'fr' ? 'en' : 'fr');
    };

    return (
        <div className="home-container">
            {/* Bannière scores retardés */}
            <motion.div 
                className="delayed-scores-banner warning"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
            >
                <FaExclamationTriangle className="warning-icon" />
                <p>
                    {language === 'fr' 
                        ? 'Les scores sont retardés de quelques minutes'
                        : 'Scores are delayed by a few minutes'}
                </p>
            </motion.div>

            {/* Bannière challenge principale */}
            <motion.div 
                className="challenge-banner"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
            >
                <div className="banner-content">
                    <FaTrophy className="banner-icon" />
                    <div className="banner-text">
                        <h1>
                            {language === 'fr' 
                                ? `Tente d’obtenir jusqu’à ${config.gigasOffered * 10} Go d’internet mobile valables 30 jours sur tes compétitions préférées !`
                                : `Try to get up to ${config.gigasOffered * 10} GB of mobile internet valid for 30 days on your favorite competitions!`}
                        </h1>
                        <p>
                            {language === 'fr' 
                                ? `${config.gigasOffered} Go de data mobile offerts pour chaque bonne prédiction`
                                : `${config.gigasOffered} GB of mobile data offered for each correct prediction`}
                        </p>
                    </div>
                </div>
            </motion.div>

            {/* Section Comment jouer */}
            <motion.section 
                className="how-to-play-section"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
            >
                <h2>
                    {language === 'fr' ? 'Comment jouer à GigaScore ?' : 'How to play GigaScore?'}
                </h2>
                <div className="steps-container">
                    <motion.div 
                        className="step"
                        whileHover={{ scale: 1.05 }}
                    >
                        <div className="step-number">1</div>
                        <FaFutbol className="step-icon" />
                        <h3>
                            {language === 'fr' 
                                ? 'Choisis ton match'
                                : 'Choose your match'}
                        </h3>
                        <p>
                            {language === 'fr'
                                ? 'Sélectionne un match parmi les compétitions disponibles'
                                : 'Select a match from available competitions'}
                        </p>
                    </motion.div>

                    <motion.div 
                        className="step"
                        whileHover={{ scale: 1.05 }}
                    >
                        <div className="step-number">2</div>
                        <FaPen className="step-icon" />
                        <h3>
                            {language === 'fr'
                                ? 'Fais ta prédiction'
                                : 'Make your prediction'}
                        </h3>
                        <p>
                            {language === 'fr'
                                ? 'Entre ta prédiction pour le résultat du match'
                                : 'Enter your prediction for the match result'}
                        </p>
                    </motion.div>

                    <motion.div 
                        className="step"
                        whileHover={{ scale: 1.05 }}
                    >
                        <div className="step-number">3</div>
                        <FaGift className="step-icon" />
                        <h3>
                            {language === 'fr'
                                ? `Reçois ${config.gigasOffered} Go`
                                : `Get ${config.gigasOffered} GB`}
                        </h3>
                        <p>
                            {language === 'fr'
                                ? 'Reçois ta data mobile offerte si ta prédiction est correcte'
                                : 'Get your mobile data if your prediction is correct'}
                        </p>
                    </motion.div>
                </div>
            </motion.section>

            {/* Filtres rapides */}
            <motion.section 
                className="filters-section"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
            >
                <div className="filters-container">
                    <div className="filter-group">
                        <FaFilter className="filter-icon" />
                        <select 
                            value={filters.competition} 
                            onChange={(e) => {
                                setFilters(prev => ({ ...prev, competition: e.target.value }));
                                // Google Analytics event: filter competition
                                if (window.gtag) {
                                    window.gtag('event', 'filter_competition', {
                                        competition_code: e.target.value,
                                        competition_name: allCompetitions.find(c => c.code === e.target.value)?.name || e.target.value
                                    });
                                }
                            }}
                            className="filter-select"
                        >
                            <option value="ALL">
                                {language === 'fr' ? 'Toutes les compétitions' : 'All competitions'}
                            </option>
                            {allCompetitions.map(({ code, name }) => (
                                <option key={code} value={code}>{name}</option>
                            ))}
                        </select>
                    </div>

                    <div className="filter-group">
                        <FaClock className="filter-icon" />
                        <select 
                            value={filters.status} 
                            onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                            className="filter-select"
                        >
                            <option value="all">
                                {language === 'fr' ? 'Tous les statuts' : 'All statuses'}
                            </option>
                            <option value="upcoming">
                                {language === 'fr' ? 'À venir' : 'Upcoming'}
                            </option>
                            <option value="live">
                                {language === 'fr' ? 'En cours' : 'Live'}
                            </option>
                            <option value="finished">
                                {language === 'fr' ? 'Terminés' : 'Finished'}
                            </option>
                        </select>
                    </div>

                    <div className="filter-group">
                        <FaCalendar className="filter-icon" />
                        <select 
                            value={filters.date} 
                            onChange={(e) => setFilters(prev => ({ ...prev, date: e.target.value }))}
                            className="filter-select"
                        >
                            <option value="all">
                                {language === 'fr' ? 'Toutes les dates' : 'All dates'}
                            </option>
                            <option value="today">
                                {language === 'fr' ? 'Aujourd\'hui' : 'Today'}
                            </option>
                            <option value="tomorrow">
                                {language === 'fr' ? 'Demain' : 'Tomorrow'}
                            </option>
                            <option value="week">
                                {language === 'fr' ? 'Cette semaine' : 'This week'}
                            </option>
                        </select>
                    </div>

                    <button 
                        className="language-toggle"
                        onClick={toggleLanguage}
                    >
                        <FaGlobe />
                        {language === 'fr' ? 'EN' : 'FR'}
                    </button>

                    <button 
                        className="reset-filters-button"
                        onClick={() => setFilters({
                            competition: 'all',
                            status: 'all',
                            date: 'all'
                        })}
                        title={language === 'fr' ? 'Réinitialiser les filtres' : 'Reset filters'}
                    >
                        <FaSync />
                        {language === 'fr' ? 'Reset' : 'Reset'}
                    </button>
                </div>
            </motion.section>

            {/* Section des matchs */}
            <div className="matches-section">
                <div className="matches-header">
                    <div className="matches-title-section">
                        <FaTrophy className="matches-trophy-icon" />
                    <h2>
                            {language === 'fr' ? 'Matchs disponibles' : 'Available matches'}
                        </h2>
                        <p className="matches-subtitle">
                            {language === 'fr' 
                                ? 'Choisis un match et tente de gagner des Go' 
                                : 'Choose a match and try to win GB'}
                        </p>
                    </div>
                    <motion.button
                        className="refresh-button"
                        onClick={handleRefresh}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        disabled={loading}
                    >
                        <FaSync className={loading ? 'spinning' : ''} />
                        {language === 'fr' ? 'Rafraîchir' : 'Refresh'}
                    </motion.button>
                </div>

                {error ? (
                    <div className="error-message">
                        <FaClock />
                        <p>{error}</p>
                    </div>
                ) : loading ? (
                    <div className="loading-spinner">
                        <div className="spinner"></div>
                        <p>
                            {language === 'fr' ? 'Chargement des matchs...' : 'Loading matches...'}
                        </p>
                    </div>
                ) : filteredMatches.length === 0 ? (
                    <div className="no-matches">
                        <FaFutbol className="no-matches-icon" />
                        <div className="no-matches-content">
                            <h3>
                                {language === 'fr' 
                                    ? 'Aucun match disponible pour le moment'
                                    : 'No matches available at the moment'}
                            </h3>
                            <p>
                                {language === 'fr' 
                                    ? 'Pas de panique ! Les matchs sont mis à jour en temps réel. Reviens dans quelques minutes pour voir les nouveaux défis disponibles. ⚽'
                                    : 'Don\'t worry! Matches are updated in real-time. Come back in a few minutes to see new challenges available. ⚽'}
                            </p>
                            <div className="no-matches-tips">
                        <p>
                            {language === 'fr' 
                                        ? '💡 Conseil : Les matchs apparaissent généralement 1-2 heures avant le coup d\'envoi'
                                        : '💡 Tip: Matches usually appear 1-2 hours before kick-off'}
                        </p>
                            </div>
                        </div>
                    </div>
                ) : (
                    <AnimatePresence>
                        <div className="matches-grid">
                            {filteredMatches.map((match) => (
                                <MatchCard
                                    key={match.id}
                                    match={match}
                                    onParticipate={handleParticipate}
                                    isParticipated={participatedMatches.has(match.id)}
                                />
                            ))}
                        </div>
                    </AnimatePresence>
                )}

                {/* Bannière scores retardés en bas */}
                <div className="delayed-scores-banner bottom">
                    <FaClock className="delayed-icon" />
                    <p>
                        {language === 'fr' 
                            ? '⚠ Les horaires et résultats peuvent être retardés selon la compétition.' 
                            : '⚠ Kick-off times and results may be delayed depending on the competition.'}
                    </p>
                </div>
            </div>

            {/* Footer simple */}
            <footer className="footer">
                <div className="footer-content">
                    <p>
                        {language === 'fr' 
                            ? 'Zéro pari d\'argent, 100% fun et data mobile à recevoir !'
                            : 'Zero money betting, 100% fun and mobile data to receive!'}
                    </p>
                    <div className="footer-links">
                        <a href="#support">
                            {language === 'fr' ? 'Support' : 'Support'}
                        </a>
                        <a href="#cgu">
                            {language === 'fr' ? 'CGU' : 'Terms'}
                        </a>
                    </div>
                    <div className="footer-copyright">
                        <p>© 2025 Bazo Capital Inc</p>
                    </div>
                </div>
            </footer>

            {/* Modal de bienvenue */}
            <WelcomeModal
                isOpen={isWelcomeModalOpen}
                onClose={handleWelcomeModalClose}
                language={language}
            />

            {/* Modal de participation */}
            <ParticipationModal
                isOpen={isModalOpen}
                onClose={handleModalClose}
                match={selectedMatch}
                onSuccess={() => handleParticipationSuccess(selectedMatch?.id)}
                language={language}
            />
        </div>
    );
};

export default Home; 